import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  MyLocation as MyLocationIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../utils/userPermissions';
import {
  hardwareAPI,
  companiesAPI,
  type Logger,
  type LicenceType,
  type DeviceActionResult,
} from '../../services';
import {
  HARDWARE_STATUS_OPTIONS,
  HardwareImage,
  assignmentInfo,
  batteryInfo,
  connectionState,
  displayValue,
  formatDateOnly,
  formatRelative,
  frequencyLabel,
  hardwareStatusLabel,
  hasLoggingFrequency,
  isConxagent,
  lastSeen,
  modeLabel,
  signalInfo,
  statusMeta,
} from './hardwareDisplay';
import { ChangeFrequencyDialog, ChangeModeDialog } from './DeviceActionDialogs';
import {
  CALIBRATION_FREQUENCY_OPTIONS,
  HDTECH_COMPANY_ID,
  PAYMENT_TERMS,
  SIM_STATUS_OPTIONS,
  SIM_TYPE_OPTIONS,
  SUBSCRIPTION_OPTIONS,
  addMonthsToDateInput,
  fromLogger,
  isAgentType,
  isHire,
  isPolledWeatherSerial,
  isScheduledLoggerType,
  isTankType,
  isWeatherType,
  toUpdatePayload,
  type CompanyOption,
  type FormState,
} from './hardwareFormModel';

interface Props {
  id: string;
}

// Centred at a readable width, like the mockup: form left, device right.
const PAGE_MAX_WIDTH = 1240;
const FORM_LABEL_WIDTH = 200;
const STATUS_LABEL_WIDTH = 180;

/** Mockup-style form row: right-aligned label, field to its right. */
const Row: React.FC<{ label: string; htmlFor?: string; labelId?: string; children: React.ReactNode }> = ({
  label, htmlFor, labelId, children,
}) => (
  <Box
    className="hw-row"
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: `${FORM_LABEL_WIDTH}px minmax(0, 1fr)` },
      alignItems: 'center',
      columnGap: 2,
      rowGap: 0.25,
      minHeight: 40,
      py: 0.4,
    }}
  >
    <Typography
      component="label"
      htmlFor={htmlFor}
      id={labelId}
      variant="body2"
      sx={{ textAlign: { sm: 'right' }, color: 'text.primary' }}
    >
      {label}
    </Typography>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>
);

const Value: React.FC<{ value?: React.ReactNode; tone?: 'error' }> = ({ value, tone }) => (
  <Typography
    variant="body2"
    sx={{
      fontWeight: 600,
      color: value ? (tone === 'error' ? 'error.main' : 'text.primary') : 'text.disabled',
      overflowWrap: 'anywhere',
    }}
  >
    {value || '—'}
  </Typography>
);

const GroupTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="overline" color="text.secondary" sx={{ display: 'block', fontWeight: 700, letterSpacing: '0.08em', mt: 1.5, mb: 0.25 }}>
    {children}
  </Typography>
);

/** Right-panel status line: right-aligned label, value, optional button. */
const StatusLine: React.FC<{ label: string; children: React.ReactNode; action?: React.ReactNode }> = ({ label, children, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minHeight: 34 }}>
    <Typography variant="body2" sx={{ width: STATUS_LABEL_WIDTH, flexShrink: 0, textAlign: 'right' }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0, typography: 'body2', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, overflowWrap: 'anywhere' }}>
      {children}
    </Box>
    {action}
  </Box>
);

// Square outlined icon button, as drawn in the mockup.
const squareBtn = { border: '1px solid', borderColor: 'primary.main', borderRadius: 1, color: 'primary.main', p: 0.5 } as const;

/**
 * One page per device, laid out like the manager's mockup: the field form
 * on the left (inputs for HDTECH, text for everyone else), the product
 * photo, live status and the connection to ConXedge on the right.
 */
const HardwareDevicePage: React.FC<Props> = ({ id }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = hasPermission(user, 'hardware.edit_advanced');
  const canEditBasic = hasPermission(user, 'hardware.edit_basic');

  const [logger, setLogger] = useState<Logger | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [original, setOriginal] = useState<FormState | null>(null);
  const [licenceTypes, setLicenceTypes] = useState<LicenceType[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [livePourWarning, setLivePourWarning] = useState('');
  const [locating, setLocating] = useState(false);
  const [modeTarget, setModeTarget] = useState<Logger | null>(null);
  const [freqTarget, setFreqTarget] = useState<Logger | null>(null);

  const load = useCallback(async () => {
    try {
      const l = await hardwareAPI.getLoggerById(id);
      setLogger(l);
      const f = fromLogger(l);
      setForm(f);
      setOriginal(f);
      setError('');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load hardware');
    } finally {
      setLoading(false);
    }
    // Only the device id may trigger a reload: a reload resets unsaved edits.
  }, [id]);

  useEffect(() => {
    load();
    hardwareAPI.getLicenceTypes().then((l) => setLicenceTypes(Array.isArray(l) ? l : [])).catch(() => {});
  }, [load]);

  useEffect(() => {
    if (!canEdit) return;
    companiesAPI
      .getCompanies()
      .then((list) =>
        setCompanies(
          (Array.isArray(list) ? list : [])
            // Only active clients can take hardware (legacy Status '01')
            .filter((c: any) => String(c.companyid) !== HDTECH_COMPANY_ID && c.isactive !== false)
            .map((c: any) => ({ id: String(c.companyid), name: c.companyname }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        ),
      )
      .catch(() => {});
  }, [canEdit]);

  const dirty = !!form && !!original && JSON.stringify(form) !== JSON.stringify(original);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!logger || !form) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/hardware')} sx={{ textTransform: 'none', mb: 2 }}>
          {t('hardware.backToList', 'Back to Hardware')}
        </Button>
        <Alert severity="error">{error || t('hardware.notFound', 'Hardware not found')}</Alert>
      </Box>
    );
  }

  const edit = canEdit;
  const fine = form.Type;
  const agent = isAgentType(fine);
  const scheduled = isScheduledLoggerType(fine);
  const weather = isWeatherType(fine);
  const tank = isTankType(fine);
  const hiring = isHire(form.SaleOrHire);
  const companyLocked = !!logger.OnLivePour && !hiring;
  const assignment = assignmentInfo(logger, t);
  const status = statusMeta(logger.TrackStatus);
  const battery = batteryInfo(logger.BatteryLevel as unknown as string);
  const signal = signalInfo(logger.SignalStrengh as unknown as string);
  const seen = lastSeen(logger);
  const connection = connectionState(logger);
  const hasFix = logger.Latitude != null && logger.Longitude != null;
  const canRequestLocation = (fine === '01' || fine === '10') && canEditBasic;
  const canChangeMode = isConxagent(logger) && canEdit;
  const canChangeFrequency = hasLoggingFrequency(logger) && canEditBasic;
  const today = new Date().toISOString().slice(0, 10);
  const licenceEnd = addMonthsToDateInput(form.LicenceStartDate, form.SubscriptionMonths);
  const licenceExpired = !!licenceEnd && licenceEnd < today;
  const nextCalibration = addMonthsToDateInput(form.CalibrationEffectiveDate, form.CalibrationFrequencyNumber);
  const selectedCompany = companies.find((c) => c.id === form.CompanyID) ?? null;

  const set = (field: keyof FormState) => (value: string) =>
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  const fid = (field: string) => `hw-${field}`;

  // Row renderers: an input for HDTECH, plain text for everyone else.
  const text = (field: keyof FormState, label: string, extra?: Partial<React.ComponentProps<typeof TextField>>) => (
    <Row label={label} htmlFor={edit ? fid(field) : undefined}>
      {edit ? (
        <TextField id={fid(field)} fullWidth size="small" value={form[field]} onChange={(e) => set(field)(e.target.value)} {...extra} />
      ) : <Value value={form[field]} />}
    </Row>
  );
  const date = (field: keyof FormState, label: string) => (
    <Row label={label} htmlFor={edit ? fid(field) : undefined}>
      {edit ? (
        <TextField id={fid(field)} fullWidth size="small" type="date" value={form[field]} onChange={(e) => set(field)(e.target.value)} />
      ) : <Value value={form[field] ? formatDateOnly(form[field]) : ''} />}
    </Row>
  );
  const select = (
    field: keyof FormState,
    label: string,
    options: { value: string; label: string }[],
    opts?: { emptyLabel?: string; unknownLabel?: (v: string) => string },
  ) => {
    const value = form[field];
    const known = options.some((o) => o.value === value);
    const unknownLabel = opts?.unknownLabel ? opts.unknownLabel(value) : value;
    return (
      <Row label={label} labelId={`${fid(field)}-label`}>
        {edit ? (
          <TextField
            select fullWidth size="small" id={fid(field)} value={value}
            onChange={(e) => set(field)(e.target.value)}
            slotProps={{ select: { displayEmpty: true, labelId: `${fid(field)}-label` } }}
          >
            <MenuItem value=""><em>{opts?.emptyLabel || t('common.none', 'None')}</em></MenuItem>
            {options.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
            {value && !known && <MenuItem value={value}>{unknownLabel}</MenuItem>}
          </TextField>
        ) : <Value value={options.find((o) => o.value === value)?.label || (value ? unknownLabel : '')} />}
      </Row>
    );
  };
  const computed = (label: string, value: string, tone?: 'error') => (
    <Row label={label}>
      <Value
        value={value ? `${formatDateOnly(value)}${tone === 'error' ? ` (${t('hardware.expired', 'expired')})` : ''}` : ''}
        tone={tone}
      />
    </Row>
  );

  const purposeValue = hiring ? 'For Hire' : /sale/i.test(form.SaleOrHire) ? 'For Sale' : '';

  const handleSave = async (confirmLivePour = false) => {
    if (!form.HMTechNumber.trim()) {
      setToast({ msg: t('hardware.hardwareNumberRequired', 'Hardware Number is required'), severity: 'error' });
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = toUpdatePayload(form);
      // Only send the company when it changes, so other edits still save
      // while it's locked by a live pour.
      if (payload.CompanyID === original?.CompanyID) delete payload.CompanyID;
      if (confirmLivePour) payload.ConfirmLivePour = true;
      const updated = await hardwareAPI.updateLogger(id, payload);
      setLogger(updated);
      const f = fromLogger(updated);
      setForm(f);
      setOriginal(f);
      setToast({ msg: t('hardware.saved', 'Changes saved'), severity: 'success' });
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === 'LIVE_POUR') {
        setLivePourWarning(detail.message);
      } else {
        setError(typeof detail === 'string' ? detail : t('hardware.saveFailed', 'Save failed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const leave = () => {
    if (dirty && !window.confirm(t('hardware.discardChanges', 'Discard your unsaved changes?'))) return;
    navigate('/hardware');
  };

  const handleRequestLocation = async () => {
    setLocating(true);
    try {
      const r = await hardwareAPI.requestLocation(id);
      setToast({ msg: r.message, severity: 'success' });
    } catch (e: any) {
      setToast({ msg: e?.response?.data?.detail || t('hardware.locateFailed', 'Location request failed'), severity: 'error' });
    } finally {
      setLocating(false);
    }
  };

  const onActionDone = (r: DeviceActionResult) => {
    setModeTarget(null);
    setFreqTarget(null);
    setToast({ msg: r.message, severity: r.sent_to_device ? 'success' : 'info' });
    // Frequency is stored straight away; mode updates when the logger reports
    load();
  };

  const mapsUrl = hasFix ? `https://www.google.com/maps?q=${logger.Latitude},${logger.Longitude}` : undefined;
  const connectionColor = connection.color === 'default' ? 'text.secondary' : `${connection.color}.main`;
  const smallBtn = { textTransform: 'none', whiteSpace: 'nowrap', py: 0.25 } as const;

  return (
    <Box sx={{ width: '100%', maxWidth: PAGE_MAX_WIDTH, mx: 'auto' }}>
      {/* Title bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <Tooltip title={t('hardware.backToList', 'Back to Hardware')}>
          <IconButton onClick={leave} aria-label={t('hardware.backToList', 'Back to Hardware')} sx={{ ml: -1 }}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {logger.HMTechNumber || logger.SerialNumber}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {[logger.TypeName !== 'Unknown' ? logger.TypeName : null, assignment.company].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        <Chip size="small" label={status.label} color={status.color} sx={{ fontWeight: 600 }} />
        {assignment.status && (
          <Chip size="small" variant="outlined" label={assignment.status} color={assignment.color === 'default' ? undefined : assignment.color} />
        )}
        {logger.LicenceExpired && <Chip size="small" color="error" label={t('hardware.licenceExpired', 'Licence expired')} />}
        <Box sx={{ flex: 1 }} />
        {canEdit && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {dirty && <Typography variant="body2" color="text.secondary">{t('hardware.unsaved', 'Unsaved changes')}</Typography>}
            <Button onClick={() => original && setForm(original)} disabled={!dirty || saving} sx={{ textTransform: 'none' }}>
              {t('hardware.discard', 'Discard')}
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={() => handleSave()}
              disabled={!dirty || saving}
              sx={{ textTransform: 'none' }}
            >
              {t('hardware.saveChanges', 'Save changes')}
            </Button>
          </Box>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.15fr) minmax(0, 1fr)' }, gap: 3, alignItems: 'start' }}>
        {/* Left: the mockup's field form */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper variant="outlined" sx={{ borderRadius: 2, px: { xs: 2, sm: 3 }, py: 2 }}>
            <Row label={t('hardware.hardwareSerialNumber', 'Hardware / Serial Number')}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                {edit ? <TextField size="small" value={form.HMTechNumber} disabled inputProps={{ 'aria-label': t('hardware.hardwareNumber', 'Hardware Number') }} /> : <Value value={form.HMTechNumber} />}
                {edit ? <TextField size="small" value={form.SerialNumber} disabled inputProps={{ 'aria-label': t('hardware.serialNumber', 'Serial Number') }} /> : <Value value={form.SerialNumber} />}
              </Box>
            </Row>
            {agent && text('AccessCode', t('hardware.accessCode', 'Access Code'), { inputProps: { maxLength: 4, inputMode: 'numeric' } })}
            {agent && text('ServiceCode', t('hardware.serviceCode', 'Service Code'), { inputProps: { maxLength: 4, inputMode: 'numeric' } })}
            {date('ManufacturedDate', t('hardware.manufacturedDate', 'Date Manufactured'))}
            {select('Status', t('hardware.status', 'Status'), HARDWARE_STATUS_OPTIONS)}
            <Row label={t('hardware.purpose', 'Purpose')} labelId={`${fid('SaleOrHire')}-label`}>
              {edit ? (
                <TextField
                  select fullWidth size="small" id={fid('SaleOrHire')} value={purposeValue}
                  onChange={(e) => set('SaleOrHire')(e.target.value)}
                  slotProps={{ select: { displayEmpty: true, labelId: `${fid('SaleOrHire')}-label` } }}
                >
                  <MenuItem value=""><em>{t('hardware.notSet', 'Not set')}</em></MenuItem>
                  <MenuItem value="For Sale">{t('hardware.forSale', 'For Sale')}</MenuItem>
                  <MenuItem value="For Hire">{t('hardware.forHire', 'For Hire')}</MenuItem>
                </TextField>
              ) : <Value value={displayValue(logger.SaleOrHire) !== '—' ? logger.SaleOrHire : ''} />}
            </Row>
            {select(
              'LicenceTypeID',
              t('hardware.licenceType', 'Software Licence Type'),
              licenceTypes
                .filter((lt) => lt.IsActive !== false || lt.LicenceTypeID === form.LicenceTypeID)
                .map((lt) => ({ value: lt.LicenceTypeID, label: lt.Name })),
            )}
            {date('LicenceStartDate', t('hardware.licenceStart', 'Software Licence Start Date'))}
            {select('SubscriptionMonths', t('hardware.licencePeriod', 'Software Licence Period'), SUBSCRIPTION_OPTIONS)}
            {computed(t('hardware.licenceEnd', 'Software Licence End Date'), licenceEnd, licenceExpired ? 'error' : undefined)}
            {scheduled && (
              <Row label={t('hardware.measurementFrequency', 'Measurement Frequency')}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Value value={frequencyLabel(logger.LoggerFrequency) || ''} />
                  {canChangeFrequency && (
                    <Button size="small" variant="outlined" onClick={() => setFreqTarget(logger)} sx={smallBtn}>
                      {t('hardware.changeFrequency', 'Change frequency')}
                    </Button>
                  )}
                </Box>
              </Row>
            )}
            <Row label={hiring ? t('hardware.hiredTo', 'Hired to') : t('hardware.allocatedTo', 'Allocated to')} htmlFor={edit ? fid('CompanyID') : undefined}>
              {edit ? (
                <Autocomplete
                  id={fid('CompanyID')}
                  options={companies}
                  value={selectedCompany}
                  onChange={(_, v) => set('CompanyID')(v ? v.id : '')}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  disabled={companyLocked}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder={t('hardware.notSoldOrHired', 'HDTECH (not sold or hired)')}
                      helperText={
                        companyLocked
                          ? t('hardware.companyLockedShort', 'Locked while on live pour {{pour}}', { pour: logger.CurrentPourCode || '' })
                          : undefined
                      }
                    />
                  )}
                />
              ) : <Value value={assignment.company} />}
            </Row>
            {hiring && form.CompanyID && date('HireStartDate', t('hardware.hireStart', 'Hire Start'))}
            {hiring && form.CompanyID && date('ReturnDate', t('hardware.expectedReturn', 'Expected Return'))}
            {hiring && form.CompanyID && !weather && !tank && select('PaymentTerms', t('hardware.paymentTerms', 'Payment Terms'), PAYMENT_TERMS)}
            {!hiring && form.CompanyID && date('SoldDate', t('hardware.dateSold', 'Date Sold'))}
          </Paper>

        </Box>

        {/* Right: photo, live status, connection (the mockup's right side) */}
        <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
            <HardwareImage typeName={logger.TypeName} size={180} />
          </Box>

          <StatusLine label={t('hardware.hardwareNumber', 'Hardware Number')}>{logger.HMTechNumber || '—'}</StatusLine>
          <StatusLine label={t('hardware.firmware', 'Firmware Number')}>{displayValue(logger.Firmware)}</StatusLine>
          {(agent || displayValue(logger.Mode) !== '—') && (
            <StatusLine
              label={t('hardware.currentMode', 'Current Functionality Mode')}
              action={canChangeMode ? (
                <Button size="small" variant="outlined" onClick={() => setModeTarget(logger)} sx={smallBtn}>
                  {t('hardware.changeMode', 'Change mode')}
                </Button>
              ) : undefined}
            >
              {modeLabel(logger.Mode)}
            </StatusLine>
          )}
          <StatusLine
            label={logger.OnLivePour ? t('hardware.pourCurrently', 'Pour Currently On') : t('hardware.pourLast', 'Pour Last Used On')}
            action={logger.CurrentPourID ? (
              <Tooltip title={t('hardware.openPour', 'Open pour')}>
                <IconButton size="small" component={RouterLink} to={`/pours/${logger.CurrentPourID}`} sx={squareBtn}>
                  <SearchIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : undefined}
          >
            {logger.CurrentPourCode || '—'}
          </StatusLine>
          <StatusLine label={t('hardware.loggerStatus', 'Logger Status')}>{`${status.label} · ${hardwareStatusLabel(logger.Status)}`}</StatusLine>
          <StatusLine label={t('hardware.batteryLevel', 'Battery Level')}>
            <Box component="span" sx={{ display: 'inline-flex', color: battery.color }}>{battery.icon}</Box>
            {battery.pct != null ? `${battery.pct}%` : '—'}
          </StatusLine>
          <StatusLine label={t('hardware.signalLevel', 'Signal Level')}>
            <Box component="span" sx={{ display: 'inline-flex', color: signal.color }}>{signal.icon}</Box>
            {signal.label || '—'}
          </StatusLine>
          <StatusLine label={t('hardware.lastConnectedDate', 'Last Connected Date')}>
            {seen ? (
              <Tooltip title={seen.toLocaleString()}><span>{formatRelative(seen)}</span></Tooltip>
            ) : t('hardware.noReadings', 'None received')}
          </StatusLine>
          <StatusLine
            label={t('hardware.lastLocation', 'Last Location (GPS)')}
            action={
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {canRequestLocation && (
                  <Tooltip title={t('hardware.requestLocation', 'Request location now')}>
                    <span>
                      <IconButton size="small" onClick={handleRequestLocation} disabled={locating} sx={squareBtn}>
                        {locating ? <CircularProgress size={18} /> : <MyLocationIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {mapsUrl && (
                  <Tooltip title={t('hardware.openInMaps', 'Open in Google Maps')}>
                    <IconButton size="small" component="a" href={mapsUrl} target="_blank" rel="noopener noreferrer" sx={squareBtn}>
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            }
          >
            {hasFix
              ? `${(logger.Latitude as number).toFixed(4)}, ${(logger.Longitude as number).toFixed(4)}`
              : t('hardware.noLocationShort', 'None yet')}
          </StatusLine>

          <Divider sx={{ my: 2.5 }} />

          {/* Connection to ConXedge, as in the mockup */}
          <Box data-testid="connection-strip" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <HardwareImage typeName={logger.TypeName} size={64} />
            <Box sx={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
              <Box sx={{ borderTop: '3px dashed', borderColor: connectionColor, mb: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: connectionColor }}>
                {t(`hardware.connection_${connection.key}`, connection.label)}
              </Typography>
            </Box>
            <Box component="img" src={`${process.env.PUBLIC_URL || ''}/conxedge-icon.png`} alt="ConXedge" sx={{ width: 64, height: 64, objectFit: 'contain' }} />
          </Box>

        </Box>
      </Box>

      {/* Spreadsheet fields the mockup doesn't show: a panel, closed by default */}
      <Accordion
        disableGutters
        variant="outlined"
        sx={{ borderRadius: 2, mt: 3, '&:before': { display: 'none' }, overflow: 'hidden' }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: { xs: 2, sm: 3 } }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {[
              scheduled ? t('hardware.calibrationSection', 'Calibration') : null,
              tank ? t('hardware.tankSection', 'Tank') : null,
              agent ? t('hardware.simSection', 'SIM card') : null,
              weather ? t('hardware.weatherSection', 'Weather station') : null,
              t('common.note', 'Note'),
            ].filter(Boolean).join(', ')}
          </Typography>
          {scheduled && nextCalibration && (
            <Typography variant="body2" color={nextCalibration < today ? 'error' : 'text.secondary'} sx={{ ml: 2 }}>
              {t('hardware.nextCalibrationDueOn', 'Next calibration due {{date}}', { date: formatDateOnly(nextCalibration) })}
            </Typography>
          )}
        </AccordionSummary>
        <AccordionDetails
          sx={{
            px: { xs: 2, sm: 3 }, pt: 0, pb: 2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(320px, 1fr))' },
            columnGap: 4,
            '& .hw-row': { gridTemplateColumns: { sm: '150px minmax(0, 1fr)' } },
          }}
        >
        {scheduled && (
          <Box>
            <GroupTitle>{t('hardware.calibrationSection', 'Calibration')}</GroupTitle>
            {date('CalibrationEffectiveDate', t('hardware.lastCalibration', 'Last Calibration'))}
            {select(
              'CalibrationFrequencyNumber',
              t('hardware.calibrationFrequency', 'Calibration Frequency'),
              CALIBRATION_FREQUENCY_OPTIONS,
              { unknownLabel: (v) => `${v} months` },
            )}
            {computed(
              t('hardware.nextCalibration', 'Next Calibration Due'),
              nextCalibration,
              nextCalibration && nextCalibration < today ? 'error' : undefined,
            )}
          </Box>
        )}
        {tank && (
          <Box>
            <GroupTitle>{t('hardware.tankSection', 'Tank')}</GroupTitle>
            {text('CalibrationFrequencyNumber', t('hardware.defaultTemperature', 'Default Temperature'), { type: 'number' })}
          </Box>
        )}
        {agent && (
          <Box>
            <GroupTitle>{t('hardware.simSection', 'SIM card')}</GroupTitle>
            {select('SIMStatus', t('hardware.simStatus', 'SIM Card Status'), SIM_STATUS_OPTIONS.map((v) => ({ value: v, label: v })))}
            {select('SIMType', t('hardware.simType', 'SIM Card Type'), SIM_TYPE_OPTIONS.map((v) => ({ value: v, label: v })))}
            {text('SIMCardNumber', t('hardware.simCardNumber', 'SIM Card Number'))}
            {text('SIMDataNumber', t('hardware.simDataNumber', 'SIM Data Number'))}
          </Box>
        )}
        {weather && (
          <Box>
            <GroupTitle>{t('hardware.weatherSection', 'Weather station')}</GroupTitle>
            {text('DeviceName', t('hardware.deviceName', 'Device Name'))}
            {text('Manufacture', t('hardware.manufacture', 'Manufacturer'))}
            {text('WSStationID', t('hardware.wsStationId', 'ZENTRA Serial'),
              edit && form.WSStationID.trim() && !isPolledWeatherSerial(form.WSStationID)
                ? {
                    color: 'warning',
                    helperText: t(
                      'hardware.wsSerialNotPolled',
                      'Only ZENTRA serials starting with "A" get weather readings.',
                    ),
                  }
                : undefined)}
            {!edit && form.WSStationID.trim() && !isPolledWeatherSerial(form.WSStationID) && (
              <Row label="">
                <Value
                  tone="error"
                  value={t('hardware.wsSerialNotPolled', 'Only ZENTRA serials starting with "A" get weather readings.')}
                />
              </Row>
            )}
            {/* The stored key and secret never reach the browser: a field
                left blank keeps them. */}
            {edit
              ? text('WSApiKey', t('hardware.wsApiKey', 'ZENTRA API Token'), {
                  type: 'password', autoComplete: 'new-password',
                  placeholder: logger.WSApiKeySet ? t('hardware.secretKeep', 'Set (leave blank to keep)') : '',
                })
              : <Row label={t('hardware.wsApiKey', 'ZENTRA API Token')}><Value value={logger.WSApiKeySet ? t('hardware.secretSet', 'Set') : ''} /></Row>}
            {edit
              ? text('WSApiSecret', t('hardware.wsApiSecret', 'API Secret'), {
                  type: 'password', autoComplete: 'new-password',
                  placeholder: logger.WSApiSecretSet ? t('hardware.secretKeep', 'Set (leave blank to keep)') : '',
                })
              : <Row label={t('hardware.wsApiSecret', 'API Secret')}><Value value={logger.WSApiSecretSet ? t('hardware.secretSet', 'Set') : ''} /></Row>}
          </Box>
        )}
        <Box>
          <GroupTitle>{t('common.note', 'Note')}</GroupTitle>
          {edit ? (
            <TextField
              fullWidth size="small" multiline minRows={4} maxRows={8}
              inputProps={{ 'aria-label': t('common.note', 'Note') }}
              placeholder={t('hardware.notePlaceholder', 'Anything worth knowing about this device')}
              value={form.Note}
              onChange={(e) => set('Note')(e.target.value)}
            />
          ) : (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: form.Note ? 'text.primary' : 'text.disabled' }}>
              {form.Note || t('hardware.noNote', 'No note')}
            </Typography>
          )}
        </Box>
        </AccordionDetails>
      </Accordion>

      <ChangeModeDialog logger={modeTarget} onClose={() => setModeTarget(null)} onDone={onActionDone} />
      <ChangeFrequencyDialog logger={freqTarget} onClose={() => setFreqTarget(null)} onDone={onActionDone} />

      <Dialog open={!!livePourWarning} onClose={() => setLivePourWarning('')} maxWidth="xs" fullWidth>
        <DialogTitle>{t('hardware.livePourTitle', 'Device is on a live pour')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning">{livePourWarning}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLivePourWarning('')}>{t('common.cancel', 'Cancel')}</Button>
          <Button variant="contained" color="warning" onClick={() => { setLivePourWarning(''); handleSave(true); }}>
            {t('hardware.saveAnyway', 'Save anyway')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}>
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">{toast.msg}</Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default HardwareDevicePage;
